/**
 *  Build: Editor
 *
 * @package bld-editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { createRoot } from "react-dom/client";

import { coreModules, EditorEntrypoint, Editor } from "@adapptio/editor";

import {
	registerStandalone as ExtFunctionsBaseStandalone
} from "@adapptio/ext-functions-base/src/client";

import {
	registerStandalone as ExtComponentBaseStandalone
} from "@adapptio/ext-components-base/src/client";

import {
	registerStandalone as ExtComponentProStandalone
} from "@adapptio/ext-components-pro/src/client";

import { registerRuntimeTranslations as RuntimeTranslations } from "@adapptio/translations/src/runtime";
import { registerEditorTranslations as EditorTranslations } from "@adapptio/translations/src/editor";

import "./main.scss";
import { ErrorTracker, IIconPackageURLMapping, MediaContext, TranslationContext } from "@adapptio/lib-components";
import { TranslationManager } from "@adapptio/lib-core";

interface IEditorClientConfig {
	iconPackageUrlMapping: IIconPackageURLMapping;
}

declare global {
	interface Window {
		EDITOR_CONFIG: IEditorClientConfig
	}
}

window.addEventListener("load", () => {
	new ErrorTracker({
		dsn: "https://ddfd55aa35fa4411a99f155e7be6b222@o1019000.ingest.us.sentry.io/5984885",
		disabled: false
	});

	const translationManager = new TranslationManager({});

	RuntimeTranslations(translationManager);
	EditorTranslations(translationManager);

	const queryParams = new URLSearchParams(window.location.search);
	const accessToken = queryParams.get("accessToken") 
		? atob(queryParams.get("accessToken")) 
		: undefined;

	const editor = new Editor({
		apiUrl: window.location.origin,
		accessToken,
		productSupport: {
			bugReportLink: 'https://gitlab.com/hexio-a-s/app-engine/hae/-/issues/new?type=ISSUE&initialCreationContext=list-route',
			supportMenu: [
				{
					id: 'gettingStarted',
					label: 'Getting Started',
					tooltip: 'How to get started with building of your application.',
					order: 10,
					commandId: 'view.open',
					commandArgs: ['welcome.getting-started', 'welcome.getting-started']
				},
				{
					id: 'videoTutorials',
					label: 'Video Tutorials',
					tooltip: 'Watch how to build applications with Adapptio.',
					order: 40,
					commandId: 'url.open',
					commandArgs: ['https://www.youtube.com/channel/UCFfRY6aq9mO9Hmb4w7Iz7aw/featured']
				},
				{
					id: 'discord',
					label: 'Join Discord',
					tooltip: 'Let\'s discuss your ideas and ask our community how to build modern apps fast.',
					order: 60,
					commandId: 'url.open',
					commandArgs: ['https://discord.gg/tA4TnrUZtu']
				}
			],
			inspirationMenu: [],
			gettingStarted: {
				videos: [
					{
						videoId: 'Oi4RFM2m-hc',
						title: 'Tutorial: Hello World',
						description: 'Learn how to create a simple Hello World web application using Adapptio.',
						length: '3:30',
						order: 10
					},
					{
						videoId: '5VSc1n_3tEk',
						title: 'Getting Started: #01 Dynamic Values & Expressions',
						description: 'Learn how to configure components using dynamic values, data bindings and expressions.',
						length: '2:09',
						order: 20
					},
					{
						videoId: 'fO-9XFkbm1E',
						title: 'Getting Started: #02 Data From Action',
						description: 'Learn how to load data from actions to the views.',
						length: '3:07',
						order: 30
					},
					{
						videoId: 'iuXZYt-hUS0',
						title: 'Getting Started: #03 State Variables',
						description: 'Learn how to store a custom view state using variables.',
						length: '3:21',
						order: 40
					},
					{
						videoId: 'odHoEMonSPg',
						title: 'Getting Started: Navigation & Routing',
						description: 'Learn how to create navigation and how to switch contents based on the URL address.',
						length: '9:46',
						order: 50
					}
				],
				links: []
			},
			socialLinks: [
				{
					id: 'youtube',
					url: 'https://www.youtube.com/channel/UCFfRY6aq9mO9Hmb4w7Iz7aw',
					title: 'Subscribe to Adapptio on YouTube',
					icon: 'mdi/youtube'
				},
				{
					id: 'discord',
					url: 'https://discord.gg/tA4TnrUZtu',
					title: 'Join our Discord server',
					icon: 'mdi/discord'
				}
			]
		},
		runtimeRegisterFn: (runtimeSvc) => {
			const componentRegistry = runtimeSvc.getComponentRegistry();
			const functionRegistry = runtimeSvc.getFunctionRegistry();
			const translationManager = runtimeSvc.getTranslationManager();

			ExtFunctionsBaseStandalone(functionRegistry, translationManager);
			ExtComponentBaseStandalone(componentRegistry, translationManager);
			ExtComponentProStandalone(componentRegistry, translationManager);

			RuntimeTranslations(translationManager);
			EditorTranslations(translationManager);
		}
	});

	editor.enable(coreModules.actionEditor);
	editor.enable(coreModules.asset);
	editor.enable(coreModules.configEditor);
	editor.enable(coreModules.debugTools);
	editor.enable(coreModules.endpointEditor);
	editor.enable(coreModules.integrationEditor);
	editor.enable(coreModules.overview);
	editor.enable(coreModules.readmeEditor);
	editor.enable(coreModules.themeEditor);
	editor.enable(coreModules.viewEditor);
	editor.enable(coreModules.welcome);

	const appRoot = createRoot(document.getElementById("app-container"));

	const translateFn = (domain, term, vars, fallbackValue) => {
		return translationManager.translate(domain, "en_US", term, vars, fallbackValue);
	};

	appRoot.render(
		<MediaContext.Provider
			value={{
				iconPackageUrlMapping: window.EDITOR_CONFIG.iconPackageUrlMapping
			}}
		>
			<TranslationContext.Provider
				value={{
					translate: translateFn
				}}
			>
				<EditorEntrypoint editorInstance={editor} />
			</TranslationContext.Provider>
		</MediaContext.Provider>
	);

	editor.init();
});
